
/**
 * Implements retry logic for unreliable operations
 * @param operation Function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param delayMs Initial delay between retries in milliseconds (will increase with backoff)
 * @param backoffFactor Factor by which to increase delay on each retry attempt
 * @returns Result of the operation or null if all retries failed
 */
export async function withRetry<T>(
  operation: () => Promise<T | null>,
  maxRetries: number = 3,
  delayMs: number = 1000,
  backoffFactor: number = 2
): Promise<T | null> {
  let retryCount = 0;
  let result: T | null = null;
  let currentDelay = delayMs;
  
  while (result === null && retryCount <= maxRetries) {
    if (retryCount > 0) {
      console.log(`Retry attempt ${retryCount} of ${maxRetries} after ${currentDelay}ms delay...`);
      await new Promise(resolve => setTimeout(resolve, currentDelay));
      // Apply exponential backoff
      currentDelay = Math.min(currentDelay * backoffFactor, 30000); // Cap at 30 seconds
    }
    
    try {
      result = await operation();
    } catch (error) {
      console.error(`Error during attempt ${retryCount}:`, error);
      
      // If we've reached max retries, throw the error
      if (retryCount === maxRetries) {
        throw error;
      }
    }
    
    retryCount++;
  }
  
  return result;
}

/**
 * Types of network errors that might be retryable
 */
export const RETRYABLE_ERRORS = [
  'network error',
  'timeout',
  'connection closed',
  'connection reset',
  'socket hang up',
  'ECONNREFUSED',
  'ECONNRESET',
  'ETIMEDOUT',
  'overloaded',
  'service unavailable',
  'unavailable',
  '503',
  'rate limit',
  'too many requests',
  '429'
];

/**
 * Checks if an error is likely to be resolved by retrying
 * @param error Error to check
 * @returns True if the error is retryable
 */
export function isRetryableError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || error.toString().toLowerCase();
  return RETRYABLE_ERRORS.some(phrase => 
    errorMessage.includes(phrase.toLowerCase())
  ) || (error.status === 503 || error.status === 429);
}

/**
 * Sleep for the specified number of milliseconds
 * @param ms Milliseconds to sleep
 * @returns Promise that resolves after the specified time
 */
export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
